import React, { useCallback, useEffect, useMemo, useState } from 'react'
import isEmpty from 'lodash/isEmpty'

import { Button } from 'pharmacy/src/input/button'

import { CME_CERTIFICATE_MODAL } from 'mednet-util/src/constants/modal'
import {
  CME_CERTIFICATE_ERROR_MESSAGE,
  CME_CERTIFICATE_TYPE,
  CME_CERTIFICATE_TYPE_LABEL,
  CME_REDEMPTION_TYPE,
  CME_REDEMPTION_TYPE_LABEL,
} from 'mednet-util/src/constants/cme'
import { mixpanel } from 'mednet-util/src/tracking'

import { useModalDrivers } from 'mednet-cns/src/hooks/modal'
import {
  useCreateCmeCertificate,
  useRedeemedCountsLazy,
  useRedeemedCmeActivitiesLazy,
  useRedeemedMocActivitiesLazy,
} from 'mednet-cns/src/hooks/cme'

import CmeActivitiesTable from 'components/tables/cmeActivitiesTable'
import { CmeCertificateModal } from 'components/cmeModal'

import { CmeRedemptionTypeFilter, CmeYearFilter } from './cmeFilters'
import { MocAvailableActivitiesCard } from './mocAvailableActivitiesCard'

import css from './cmeRedeemedTab.scss'

const useCmeOrMocRedeemedActiviesLazy = (
  filterType,
  filterYear,
  reloadRedeemedCmeActivitiesFlag,
  reloadRedeemedMocActivitiesFlag,
  setReloadRedeemedCmeActivitiesFlag,
  setReloadRedeemedMocActivitiesFlag
) => {
  const [
    redeemedCmeActivities,
    cmeRedeemedActivitiesRequest,
    loadRedeemedCmeActivities,
  ] = useRedeemedCmeActivitiesLazy(filterYear)

  const [
    redeemedMocActivities,
    mocRedeemedActivitiesRequest,
    loadRedeemedMocActivities,
  ] = useRedeemedMocActivitiesLazy(filterYear)

  let redeemedActivities = redeemedCmeActivities
  let redeemedActivitiesRequest = cmeRedeemedActivitiesRequest
  let loadActivities = loadRedeemedCmeActivities
  let reloadFlag = reloadRedeemedCmeActivitiesFlag
  let setReloadFlag = setReloadRedeemedCmeActivitiesFlag

  if (filterType === CME_REDEMPTION_TYPE.MOC) {
    redeemedActivities = redeemedMocActivities
    redeemedActivitiesRequest = mocRedeemedActivitiesRequest
    loadActivities = loadRedeemedMocActivities
    reloadFlag = reloadRedeemedMocActivitiesFlag
    setReloadFlag = setReloadRedeemedMocActivitiesFlag
  }

  const controlledLoadRedeemedActivities = useCallback(() => {
    if (reloadFlag) {
      loadActivities()
      setReloadFlag(false)
    } else if (
      !redeemedActivitiesRequest.isLoaded &&
      !redeemedActivitiesRequest.isLoading
    ) {
      loadActivities()
    }
  }, [
    reloadFlag,
    loadActivities,
    redeemedActivitiesRequest.isLoaded,
    redeemedActivitiesRequest.isLoading,
  ])

  return [
    redeemedActivities,
    redeemedActivitiesRequest,
    controlledLoadRedeemedActivities,
    // The raw loader always refetches when called (unlike the controlled one, which no-ops once loaded).
    loadActivities,
  ]
}

export const CmeRedeemedTab = (props) => {
  const {
    creditsPerActivity,
    isAbimMocEnabledForUser,
    isAbpMocEnabledForUser,
    minYear,
    reloadFlag,
    setReloadFlag,
    history,
    showMocCertificateModal,
  } = props

  const params = new URLSearchParams(history.location.search)
  const redeemMoc = params.get('moc')

  const [filterType, setFilterType] = useState(CME_REDEMPTION_TYPE.CME)
  const [filterYear, setFilterYear] = useState(new Date().getFullYear())

  const [
    { redeemedCmeCount, redeemedMocCount, completedCmeModuleCredits },
    redeemedCountsRequest,
    loadRedeemedCounts,
  ] = useRedeemedCountsLazy(filterYear)

  // These reload flags should stay true(if initial value of reloadFlag is true) until we get the data for the first time
  const [reloadRedeemedCmeActivitiesFlag, setReloadRedeemedCmeActivitiesFlag] =
    useState(reloadFlag)
  const [reloadRedeemedMocActivitiesFlag, setReloadRedeemedMocActivitiesFlag] =
    useState(reloadFlag)
  const [reloadRemaininMocActivitiesFlag, setReloadRemaininMocActivitiesFlag] =
    useState(reloadFlag)

  const [
    redeemedActivities,
    redeemedActivitiesRequest,
    controlledLoadRedeemedActivities,
    reloadRedeemedActivities,
  ] = useCmeOrMocRedeemedActiviesLazy(
    filterType,
    filterYear,
    reloadRedeemedCmeActivitiesFlag,
    reloadRedeemedMocActivitiesFlag,
    setReloadRedeemedCmeActivitiesFlag,
    setReloadRedeemedMocActivitiesFlag
  )

  const [openCmeCertificateModal] = useModalDrivers(
    CME_CERTIFICATE_MODAL.modalId
  )
  const createCmeCertificate = useCreateCmeCertificate()
  const [selectedRowsIds, setSelectedRowsIds] = useState({})
  const [disabledRowsIds, setDisabledRowsIds] = useState({})
  const [showRemainingMoc, setShowRemainingMoc] = useState(redeemMoc)
  const [certificateError, setCertificateError] = useState(undefined)

  useEffect(() => {
    mixpanel.track('CME Redeemed Tab Load', {})
  }, [])

  useEffect(() => {
    controlledLoadRedeemedActivities() // no need for condition, this is controlled by its own reload flag

    if (reloadFlag) {
      loadRedeemedCounts()
      setReloadFlag(false)
    } else {
      controlledLoadRedeemedActivities() //  no need for condition, this is controlled by its own request status

      if (!redeemedCountsRequest.isLoading && !redeemedCountsRequest.isLoaded) {
        loadRedeemedCounts()
      }
    }
  }, [])

  useEffect(() => {
    controlledLoadRedeemedActivities() //  no need for condition, this is controlled by its own request status

    if (!redeemedCountsRequest.isLoading && !redeemedCountsRequest.isLoaded) {
      loadRedeemedCounts()
    }
  }, [filterYear])

  useEffect(() => {
    controlledLoadRedeemedActivities() //  no need for condition, this is controlled by its own request status
  }, [filterType])

  const updateSelectedRows = useCallback(() => {
    setDisabledRowsIds(
      Object.fromEntries(
        redeemedActivities
          .map((activity) => [
            activity.cmeActivityId,
            activity.createdCertificate ? 'Certificate Created' : undefined,
          ])
          .filter(([, value]) => value !== undefined)
      )
    )
  }, [setDisabledRowsIds, redeemedActivities])

  useEffect(() => {
    updateSelectedRows()
  }, [redeemedActivities])

  const getSelectedArray = useCallback(
    (selectedRows) =>
      Object.entries(selectedRows)
        .filter(([_, isSelected]) => isSelected)
        .map(([key, _]) => key),
    []
  )

  const handleSelect = useCallback(
    (updatedSelectedRowsIds) => {
      setSelectedRowsIds(updatedSelectedRowsIds)
    },
    [setSelectedRowsIds]
  )

  const handleSelectAll = useCallback(() => {
    setSelectedRowsIds(
      Object.fromEntries(
        redeemedActivities
          .map((activity) => [
            activity.cmeActivityId,
            !activity.createdCertificate,
          ])
          .filter(([_, notCreatedCertificate]) => notCreatedCertificate)
      )
    )
  }, [setSelectedRowsIds, redeemedActivities])

  const handleDeselectAll = useCallback(() => {
    setSelectedRowsIds({})
  }, [setSelectedRowsIds])

  // Keep select-all in sync with the redeemed list. The redeem flow prepends the just-redeemed
  // activities to the list, so when the user lands here the table first sees an interim list and the
  // generic auto-select latches onto it, never re-selecting once the full list resolves (it only
  // auto-selects while the selection is empty). Re-running select-all whenever the list itself changes
  // makes the selection always reflect the full, current set of non-certified activities.
  useEffect(() => {
    handleSelectAll()
  }, [redeemedActivities, handleSelectAll])

  const handleYearFilterChange = useCallback(
    (selectedOption) => {
      if (selectedOption.value !== filterYear) {
        setSelectedRowsIds({})
        setDisabledRowsIds({})
        setFilterYear(selectedOption.value)
      }
    },
    [setFilterYear, filterYear, setSelectedRowsIds, setDisabledRowsIds]
  )

  const handleTypeFilterChange = useCallback(
    (selectedOption) => {
      if (selectedOption.value !== filterType) {
        setSelectedRowsIds({})
        setDisabledRowsIds({})
        setFilterType(selectedOption.value)
      }
    },
    [setFilterType, filterType, setSelectedRowsIds, setDisabledRowsIds]
  )

  const tableActivities = useMemo(() => {
    return redeemedActivities.map((redeemedCmeActivities) => ({
      ...redeemedCmeActivities,
      id: redeemedCmeActivities.cmeActivityId,
    }))
  }, [redeemedActivities])

  const handleCreateResult = useCallback(
    (res, onSuccess) => {
      if (res.success) {
        onSuccess()
        return
      }

      // Any failure: refetch the currently-shown list so its createdCertificate flags are current (the
      // backend may have rejected because something was certified/changed elsewhere). The refreshed list
      // re-runs select-all against the now-correct rows, so already-certified ones drop out on their own.
      reloadRedeemedActivities()

      setCertificateError(
        res.alreadyCertified
          ? CME_CERTIFICATE_ERROR_MESSAGE.alreadyCertified
          : CME_CERTIFICATE_ERROR_MESSAGE.generic
      )
    },
    [reloadRedeemedActivities, setCertificateError]
  )

  const handleCertificateButtonClick = useCallback(() => {
    setCertificateError(undefined)

    // Selection is keyed by cmeActivityId (not array index) so a reordered or transient list can't
    // shift a selection onto a different activity. Already-certified rows (including completed
    // modules, which always carry a certificate) are excluded as a final guard.
    const certificateActivitiesIds = redeemedActivities
      .filter((activity) => selectedRowsIds[activity.cmeActivityId])
      .filter((activity) => !activity.createdCertificate)
      .map((activity) => activity.cmeActivityId)

    // Bail before the request if nothing certifiable is selected (everything unchecked, or every
    // selected row is already certified) — otherwise an empty list posts and the user gets a silent
    // no-op (the backend's empty-selection response isn't surfaced as a useful message here).
    if (isEmpty(certificateActivitiesIds)) {
      setCertificateError(CME_CERTIFICATE_ERROR_MESSAGE.noActivitiesSelected)
      return
    }

    // Should still show because past MOC credits could be redeemed without the needed profile data
    if (
      certificateToCreateTypes.some(
        (type) =>
          type === CME_CERTIFICATE_TYPE.ABIM_MOC ||
          type === CME_CERTIFICATE_TYPE.ABP_MOC
      )
    ) {
      mixpanel.track('Clicked Create MOC Certificate')
      if (showMocCertificateModal) {
        openCmeCertificateModal({
          certificateActivitiesIds,
          certificateTypes: certificateToCreateTypes,
          history,
          // Refresh the list behind the modal on failure (e.g. alreadyCertified) so its
          // createdCertificate flags are current when the user closes the modal and retries.
          onCreateFailure: reloadRedeemedActivities,
        })
      } else {
        mixpanel.track('created_moc_certificate')
        createCmeCertificate(
          { types: certificateToCreateTypes },
          certificateActivitiesIds,
          (res) =>
            handleCreateResult(res, () => history.push('/cme?tab=Certificates'))
        )
      }
    } else {
      mixpanel.track('Clicked Create CME Certificate')
      createCmeCertificate(
        { types: [CME_CERTIFICATE_TYPE.CME] },
        certificateActivitiesIds,
        (res) =>
          handleCreateResult(res, () => {
            mixpanel.track('created_cme_certificate')
            history.push('/cme?tab=Certificates')
          })
      )
    }
  }, [
    isAbimMocEnabledForUser,
    isAbpMocEnabledForUser,
    redeemedActivities,
    selectedRowsIds,
    handleCreateResult,
    reloadRedeemedActivities,
  ])

  const handleViewRemainingMocButtonClick = useCallback(() => {
    setShowRemainingMoc(true)
    mixpanel.track('Viewed Remaining MOC activities')
  }, [setShowRemainingMoc])

  const certificateToCreateTypes = useMemo(() => {
    return filterType === CME_REDEMPTION_TYPE.CME
      ? [CME_CERTIFICATE_TYPE.CME]
      : isAbimMocEnabledForUser && isAbpMocEnabledForUser // This should never happen but in case we had some users like these in future
      ? [CME_CERTIFICATE_TYPE.ABIM_MOC, CME_CERTIFICATE_TYPE.ABP_MOC]
      : isAbimMocEnabledForUser
      ? [CME_CERTIFICATE_TYPE.ABIM_MOC]
      : [CME_CERTIFICATE_TYPE.ABP_MOC]
  }, [filterType, isAbimMocEnabledForUser, isAbpMocEnabledForUser])

  const createCertificateButtonText = useMemo(() => {
    return `Create ${
      certificateToCreateTypes.length === 1
        ? CME_CERTIFICATE_TYPE_LABEL[certificateToCreateTypes[0]]
        : CME_REDEMPTION_TYPE_LABEL[filterType]
    } certificate${certificateToCreateTypes.length > 1 ? 's' : ''}`
  }, [certificateToCreateTypes, filterType])

  const allowClaimingRemainingMoc = useMemo(
    () =>
      (isAbimMocEnabledForUser || isAbpMocEnabledForUser) &&
      redeemedCountsRequest.isLoaded &&
      redeemedMocCount !== redeemedCmeCount &&
      filterYear === new Date().getFullYear(),
    [
      isAbimMocEnabledForUser,
      isAbpMocEnabledForUser,
      redeemedCountsRequest.isLoaded,
      redeemedMocCount,
      redeemedCmeCount,
      filterYear,
    ]
  )

  if (showRemainingMoc && (isAbimMocEnabledForUser || isAbpMocEnabledForUser)) {
    return (
      <div className={css.mocActivitiesCardContainer}>
        <MocAvailableActivitiesCard
          closeCard={() => setShowRemainingMoc(false)}
          isAbimMocEnabledForUser={isAbimMocEnabledForUser}
          isAbpMocEnabledForUser={isAbpMocEnabledForUser}
          creditsPerActivity={creditsPerActivity}
          redeemedCount={redeemedMocCount}
          reloadFlag={reloadRemaininMocActivitiesFlag}
          setReloadFlag={setReloadRemaininMocActivitiesFlag}
        />
      </div>
    )
  }

  return (
    <div className={css.mainContainer}>
      <div className={css.header}>
        <div className={css.titleMainText}>
          Redeemed CME Credits:{' '}
          <span className={css.credits}>
            {(redeemedCmeCount ?? 0) * creditsPerActivity +
              (completedCmeModuleCredits ?? 0)}
          </span>
        </div>
        <div className={css.mocPointsContainer}>
          {/* This is just to keep the main text in the center  */}
          {allowClaimingRemainingMoc && (
            <div className={css.hiddenMocPointsDiv}></div>
          )}

          {(isAbimMocEnabledForUser || isAbpMocEnabledForUser) && (
            <div className={css.titleMainText}>
              Redeemed MOC Points:{' '}
              <span className={css.credits}>
                {redeemedMocCount * creditsPerActivity}
              </span>
            </div>
          )}

          {allowClaimingRemainingMoc && (
            <div
              className={css.claimMoc}
              onClick={handleViewRemainingMocButtonClick}
            >
              View & Claim Remaining MOC
            </div>
          )}
        </div>
      </div>

      <div className={css.tableHeader}>
        <div className={css.displayForLargeScreen}>
          <Button
            type="dark_blue"
            className={css.button}
            isDisabled={isEmpty(getSelectedArray(selectedRowsIds))}
            onClick={handleCertificateButtonClick}
          >
            <div className={css.buttonText}>{createCertificateButtonText}</div>
          </Button>
        </div>
        <div className={css.filtersContainer}>
          <CmeYearFilter
            minYear={minYear}
            handleFilterChange={handleYearFilterChange}
          />
          <CmeRedemptionTypeFilter
            isAbimMocEnabledForUser={isAbimMocEnabledForUser}
            isAbpMocEnabledForUser={isAbpMocEnabledForUser}
            handleFilterChange={handleTypeFilterChange}
          />
        </div>
      </div>

      {certificateError && (
        <div className={css.certificateError}>{certificateError}</div>
      )}

      <div className={css.tableContainer}>
        <CmeActivitiesTable
          cmeActivities={tableActivities}
          isLoading={redeemedActivitiesRequest.isLoading}
          loadingDataError={redeemedActivitiesRequest.isError}
          selectedRowsIds={selectedRowsIds}
          handleSelect={handleSelect}
          handleSelectAll={handleSelectAll}
          handleDeselectAll={handleDeselectAll}
          disabledRowsIds={disabledRowsIds}
          emptyDataMessage="You have no redeemed activities in the selected year"
          isRedeemed
        />
      </div>

      <div className={css.tableFooter}>
        <div className={css.selectedCreditsText}>
          <div className={css.credits}>
            {getSelectedArray(selectedRowsIds).length * creditsPerActivity}
            &nbsp;&nbsp;
          </div>
          <div>Credits Selected</div>
        </div>
        <div className={css.displayForSmallScreen}>
          <Button
            type="dark_blue"
            className={css.button}
            isDisabled={isEmpty(getSelectedArray(selectedRowsIds))}
            onClick={handleCertificateButtonClick}
          >
            <div className={css.buttonText}>{createCertificateButtonText}</div>
          </Button>
        </div>
      </div>
      <CmeCertificateModal />
    </div>
  )
}
